import { createReadStream } from "node:fs";

export interface JsonlLine {
  text: string;
  /** Absolute byte offset just past this line (incl. its newline when terminated). */
  endOffset: number;
  /** false only for a trailing line with no newline (likely mid-write). */
  terminated: boolean;
}

const NL = 0x0a;
const CR = 0x0d;

/**
 * Streams a JSONL file line by line with exact byte-offset accounting so scans
 * can resume incrementally. Splitting on 0x0A is safe in UTF-8 (no multi-byte
 * sequence contains it). Tolerates multi-megabyte lines: memory use is bounded
 * by the largest single line, never the file size.
 */
export async function* iterateJsonlLines(path: string, fromOffset = 0): AsyncGenerator<JsonlLine> {
  const stream = createReadStream(path, { start: fromOffset });
  let pending: Buffer[] = [];
  let pendingBytes = 0;
  let offset = fromOffset;

  for await (const chunk of stream as AsyncIterable<Buffer>) {
    let start = 0;
    while (start < chunk.length) {
      const idx = chunk.indexOf(NL, start);
      if (idx === -1) {
        pending.push(chunk.subarray(start));
        pendingBytes += chunk.length - start;
        break;
      }
      const slice = chunk.subarray(start, idx);
      const lineBuf = pending.length > 0 ? Buffer.concat([...pending, slice]) : slice;
      pending = [];
      pendingBytes = 0;
      offset += lineBuf.length + 1;
      yield { text: lineText(lineBuf), endOffset: offset, terminated: true };
      start = idx + 1;
    }
  }

  if (pendingBytes > 0) {
    const lineBuf = Buffer.concat(pending);
    yield { text: lineText(lineBuf), endOffset: offset + lineBuf.length, terminated: false };
  }
}

function lineText(buf: Buffer): string {
  const end = buf.length > 0 && buf[buf.length - 1] === CR ? buf.length - 1 : buf.length;
  return buf.toString("utf8", 0, end);
}
